import { md5 } from '@noble/hashes/legacy';

/**
 * Text encryption built on native WebCrypto (SubtleCrypto).
 *
 * Current format (`v2:` prefix): PBKDF2-SHA256 derived key + AES-256-GCM
 * (authenticated), serialized as `v2:base64(salt | iv | ciphertext)`.
 *
 * Legacy format (no prefix): output of crypto-js `AES.encrypt(text, secret)` -
 * OpenSSL EVP_BytesToKey (MD5) key derivation + AES-256-CBC, base64 with
 * "Salted__" header. Supported for decryption only, so values encrypted
 * before the crypto-js removal (persisted in local/session storage) keep
 * working. New encryptions always produce the v2 format.
 */

const V2_PREFIX = 'v2:';
/** OWASP recommended minimum for PBKDF2-SHA256 */
const PBKDF2_ITERATIONS = 310_000;
const SALT_LENGTH = 16;
/** Standard nonce size for AES-GCM */
const IV_LENGTH = 12;

/** Base64 of the "Salted__" header every crypto-js/OpenSSL output starts with */
const LEGACY_OPENSSL_MAGIC = 'U2FsdGVkX1';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
};

const base64ToBytes = (base64: string) =>
  Uint8Array.from(atob(base64), character => character.charCodeAt(0));

const concatBytes = (...arrays: Uint8Array[]): Uint8Array<ArrayBuffer> => {
  const result = new Uint8Array(
    arrays.reduce((total, array) => total + array.length, 0)
  );
  let offset = 0;
  for (const array of arrays) {
    result.set(array, offset);
    offset += array.length;
  }
  return result;
};

// Key derivation is the expensive part (PBKDF2 iterations) and decryption
// runs on every signer creation, so derived keys are cached per secret+salt
const derivedKeyCache = new Map<string, Promise<CryptoKey>>();

const deriveAesGcmKey = (secret: string, salt: Uint8Array<ArrayBuffer>) => {
  const cacheKey = `${bytesToBase64(salt)} ${secret}`;
  const cached = derivedKeyCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const keyPromise = (async () => {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      textEncoder.encode(secret),
      'PBKDF2',
      false,
      ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        hash: 'SHA-256',
        salt,
        iterations: PBKDF2_ITERATIONS,
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  })();
  derivedKeyCache.set(cacheKey, keyPromise);
  return keyPromise;
};

export const encryptText = async (
  plaintext: string,
  secret: string
): Promise<string> => {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const key = await deriveAesGcmKey(secret, salt);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    textEncoder.encode(plaintext)
  );
  const payload = concatBytes(salt, iv, new Uint8Array(ciphertext));
  return `${V2_PREFIX}${bytesToBase64(payload)}`;
};

export const decryptText = async (
  encrypted: string,
  secret: string
): Promise<string> => {
  try {
    if (encrypted.startsWith(V2_PREFIX)) {
      return await decryptV2(encrypted, secret);
    }
    if (encrypted.startsWith(LEGACY_OPENSSL_MAGIC)) {
      return await decryptLegacyCryptoJs(encrypted, secret);
    }
  } catch {
    throw new Error('Decryption failed - wrong secret or corrupted data.');
  }
  throw new Error('Decryption failed - unknown encrypted data format.');
};

const decryptV2 = async (encrypted: string, secret: string) => {
  const payload = base64ToBytes(encrypted.slice(V2_PREFIX.length));
  const salt = payload.slice(0, SALT_LENGTH);
  const iv = payload.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
  const ciphertext = payload.slice(SALT_LENGTH + IV_LENGTH);
  const key = await deriveAesGcmKey(secret, salt);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );
  return textDecoder.decode(decrypted);
};

/**
 * OpenSSL EVP_BytesToKey with MD5 - derives the AES-256-CBC key (32 bytes)
 * and iv (16 bytes) the same way crypto-js `AES.encrypt(text, secret)` did
 */
const legacyEvpBytesToKey = (secret: string, salt: Uint8Array) => {
  const password = textEncoder.encode(secret);
  let block: Uint8Array = new Uint8Array(0);
  let derived: Uint8Array<ArrayBuffer> = new Uint8Array(0);
  while (derived.length < 48) {
    block = md5(concatBytes(block, password, salt));
    derived = concatBytes(derived, block);
  }
  return { key: derived.slice(0, 32), iv: derived.slice(32, 48) };
};

const decryptLegacyCryptoJs = async (encrypted: string, secret: string) => {
  // Removal signal: once this event stops appearing in PostHog,
  // all active users are migrated to v2 and the legacy path can be deleted.
  // Lazy import to keep this low-level module out of the redux import graph.
  import('#/lib/analytics')
    .then(({ trackEventTyped }) =>
      trackEventTyped({ event: 'wallet_legacy_encryption_decrypt' })
    )
    .catch(console.warn);

  const bytes = base64ToBytes(encrypted);
  // Layout: "Salted__" header (8 bytes) | salt (8 bytes) | ciphertext
  const salt = bytes.slice(8, 16);
  const ciphertext = bytes.slice(16);
  const { key: rawKey, iv } = legacyEvpBytesToKey(secret, salt);
  const key = await crypto.subtle.importKey('raw', rawKey, 'AES-CBC', false, [
    'decrypt',
  ]);
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv },
    key,
    ciphertext
  );
  return textDecoder.decode(decrypted);
};
